import { z } from "zod";
import { BusinessDate, EntityBase, Name, Note } from "./common.js";
import { Id } from "./ids.js";
import { Rial } from "./money.js";
import { MobilePhone } from "./phone.js";

/** Employment kind: رسمی، قراردادی، روزمزد، موقت. */
export const EmploymentKind = z.enum(["official", "contractual", "daily", "temporary"]);
export type EmploymentKind = z.infer<typeof EmploymentKind>;

export const EMPLOYMENT_KIND_LABELS: Record<EmploymentKind, string> = {
  official: "رسمی",
  contractual: "قراردادی",
  daily: "روزمزد",
  temporary: "موقت",
};

export const EmergencyContact = z.object({
  name: z.string().min(1).max(120),
  phone: z.string().min(1).max(40),
  relation: z.string().max(80).optional().default(""),
});
export type EmergencyContact = z.infer<typeof EmergencyContact>;

export const PositionInput = z.object({
  name: Name,
  description: z.string().max(4000).default(""),
  checklist: z.array(z.string().min(1).max(250)).default([]),
});
export type PositionInput = z.infer<typeof PositionInput>;

export const Position = EntityBase.extend({
  name: Name,
  description: z.string(),
  checklist: z.array(z.string()),
});
export type Position = z.infer<typeof Position>;

export const DocumentTypeInput = z.object({
  code: z.string().min(1).max(50),
  name: Name,
  description: z.string().max(500).optional().default(""),
  active: z.boolean().default(true),
});
export type DocumentTypeInput = z.infer<typeof DocumentTypeInput>;

export const DocumentType = EntityBase.extend({
  code: z.string(),
  name: Name,
  description: z.string(),
  active: z.boolean(),
});
export type DocumentType = z.infer<typeof DocumentType>;

export const PersonDocumentInput = z.object({
  personId: Id,
  kind: z.string().min(1).max(50),
  documentNumber: z.string().min(1).max(100),
  issueDate: BusinessDate.nullable().optional().default(null),
  expiryDate: BusinessDate,
  notes: Note.optional().default(""),
});
export type PersonDocumentInput = z.infer<typeof PersonDocumentInput>;

export const DocumentExpiryStatus = z.enum(["valid", "expiring_soon", "expired"]);
export type DocumentExpiryStatus = z.infer<typeof DocumentExpiryStatus>;

export const PersonDocument = EntityBase.extend({
  personId: Id,
  kind: z.string(),
  kindLabel: z.string().optional(),
  documentNumber: z.string(),
  issueDate: BusinessDate.nullable(),
  expiryDate: BusinessDate,
  notes: z.string(),
  notifiedStages: z.array(z.string()),
  expiryStatus: DocumentExpiryStatus.optional(),
  daysUntilExpiry: z.number().int().optional(),
});
export type PersonDocument = z.infer<typeof PersonDocument>;

export const PersonnelFileInput = z.object({
  personId: Id,
  personName: Name,
  positionId: Id.nullable().optional().default(null),
  positionName: z.string().max(120).optional().default(""),
  level: z.string().max(80).optional().default(""),
  employmentKind: EmploymentKind,
  startDate: BusinessDate,
  emergencyContact: EmergencyContact,
  basePay: Rial.nullable().optional().default(null),
  hourlyRate: Rial.nullable().optional().default(null),
  notes: Note.optional().default(""),
});
export type PersonnelFileInput = z.infer<typeof PersonnelFileInput>;

export const PersonnelFile = EntityBase.extend({
  personId: Id,
  personName: Name,
  phone: z.string().optional(),
  positionId: Id.nullable(),
  positionName: z.string(),
  level: z.string(),
  employmentKind: EmploymentKind,
  startDate: BusinessDate,
  emergencyContact: EmergencyContact,
  basePay: Rial.nullable().optional(),
  hourlyRate: Rial.nullable().optional(),
  notes: z.string(),
  documents: z.array(PersonDocument).optional(),
});
export type PersonnelFile = z.infer<typeof PersonnelFile>;

// ==========================================
// AG-39: Shifts, Attendance, Leave & Absence
// ==========================================

export const IRANIAN_WEEKDAYS = [
  { index: 0, key: "shanbeh", label: "شنبه" },
  { index: 1, key: "yekshanbeh", label: "یکشنبه" },
  { index: 2, key: "doshanbeh", label: "دوشنبه" },
  { index: 3, key: "seshanbeh", label: "سه‌شنبه" },
  { index: 4, key: "chaharshanbeh", label: "چهارشنبه" },
  { index: 5, key: "panjshanbeh", label: "پنج‌شنبه" },
  { index: 6, key: "jomeh", label: "جمعه" },
] as const;

export function getIranianDayOfWeek(isoDate: string): number {
  const d = new Date(`${isoDate}T12:00:00Z`);
  return (d.getUTCDay() + 1) % 7;
}

export const TimeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "زمان باید در قالب HH:mm باشد");

export const ShiftHeadcountRequirement = z.object({
  positionId: Id,
  positionName: z.string().optional().default(""),
  minCount: z.number().int().min(0),
});
export type ShiftHeadcountRequirement = z.infer<typeof ShiftHeadcountRequirement>;

export const ShiftTemplateInput = z.object({
  name: Name,
  startTime: TimeString,
  endTime: TimeString,
  minHeadcount: z.array(ShiftHeadcountRequirement).default([]),
  color: z.string().max(30).optional().default("#3b82f6"),
  notes: Note.optional().default(""),
});
export type ShiftTemplateInput = z.infer<typeof ShiftTemplateInput>;

export const ShiftTemplate = EntityBase.extend({
  name: Name,
  startTime: TimeString,
  endTime: TimeString,
  minHeadcount: z.array(ShiftHeadcountRequirement),
  color: z.string(),
  notes: z.string(),
});
export type ShiftTemplate = z.infer<typeof ShiftTemplate>;

export const WeeklyPatternDay = z.object({
  dayOfWeek: z.number().int().min(0).max(6),
  shiftTemplateId: Id.nullable(),
});
export type WeeklyPatternDay = z.infer<typeof WeeklyPatternDay>;

export const WeeklyShiftPatternInput = z.object({
  personId: Id,
  days: z.array(WeeklyPatternDay),
});
export type WeeklyShiftPatternInput = z.infer<typeof WeeklyShiftPatternInput>;

export const WeeklyShiftPattern = EntityBase.extend({
  personId: Id,
  days: z.array(WeeklyPatternDay),
});
export type WeeklyShiftPattern = z.infer<typeof WeeklyShiftPattern>;

export const ShiftExceptionKind = z.enum(["off", "shift", "replacement"]);
export type ShiftExceptionKind = z.infer<typeof ShiftExceptionKind>;

export const ShiftDateExceptionInput = z.object({
  personId: Id,
  date: BusinessDate,
  kind: ShiftExceptionKind,
  shiftTemplateId: Id.nullable().optional().default(null),
  replacementForPersonId: Id.nullable().optional().default(null),
  reason: Note.optional().default(""),
});
export type ShiftDateExceptionInput = z.infer<typeof ShiftDateExceptionInput>;

export const ShiftDateException = EntityBase.extend({
  personId: Id,
  date: BusinessDate,
  kind: ShiftExceptionKind,
  shiftTemplateId: Id.nullable(),
  replacementForPersonId: Id.nullable(),
  reason: z.string(),
});
export type ShiftDateException = z.infer<typeof ShiftDateException>;

export const DayShiftPlanItem = z.object({
  personId: Id,
  personName: z.string(),
  positionId: Id.nullable(),
  positionName: z.string(),
  date: BusinessDate,
  shiftTemplateId: Id.nullable(),
  shiftName: z.string(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  isOff: z.boolean(),
  isException: z.boolean(),
  exceptionKind: ShiftExceptionKind.optional(),
  replacementForPersonId: Id.nullable().optional(),
  replacementForPersonName: z.string().optional(),
});
export type DayShiftPlanItem = z.infer<typeof DayShiftPlanItem>;

export const SetAttendanceCodeInput = z.object({
  personId: Id,
  code: z.string().regex(/^\d{4,6}$/, "کد حضور باید ۴ تا ۶ رقم باشد"),
});
export type SetAttendanceCodeInput = z.infer<typeof SetAttendanceCodeInput>;

export const PunchType = z.enum(["in", "out"]);
export type PunchType = z.infer<typeof PunchType>;

export const PunchInput = z.object({
  personId: Id,
  code: z.string().regex(/^\d{4,6}$/, "کد حضور باید ۴ تا ۶ رقم باشد"),
  punchType: PunchType.optional(),
});
export type PunchInput = z.infer<typeof PunchInput>;

export const AttendancePunch = EntityBase.extend({
  personId: Id,
  personName: z.string(),
  date: BusinessDate,
  punchType: PunchType,
  punchTime: z.string(),
  deviceId: z.string(),
  source: z.enum(["tablet", "manual_approved"]),
  notes: z.string().optional(),
});
export type AttendancePunch = z.infer<typeof AttendancePunch>;

export const PunchCorrectionStatus = z.enum(["pending", "approved", "rejected"]);
export type PunchCorrectionStatus = z.infer<typeof PunchCorrectionStatus>;

export const ProposePunchCorrectionInput = z.object({
  personId: Id,
  date: BusinessDate,
  punchType: PunchType,
  proposedTime: TimeString,
  reason: z.string().min(1).max(500),
});
export type ProposePunchCorrectionInput = z.infer<typeof ProposePunchCorrectionInput>;

export const ReviewPunchCorrectionInput = z.object({
  status: z.enum(["approved", "rejected"]),
  reviewNote: Note.optional().default(""),
});
export type ReviewPunchCorrectionInput = z.infer<typeof ReviewPunchCorrectionInput>;

export const PunchCorrectionRequest = EntityBase.extend({
  personId: Id,
  personName: z.string(),
  date: BusinessDate,
  punchType: PunchType,
  proposedTime: TimeString,
  reason: z.string(),
  status: PunchCorrectionStatus,
  proposedByPersonId: Id,
  reviewedByPersonId: Id.nullable().optional(),
  reviewNote: z.string().optional(),
  reviewedAt: z.string().nullable().optional(),
});
export type PunchCorrectionRequest = z.infer<typeof PunchCorrectionRequest>;

export const LeaveKind = z.enum(["daily", "hourly", "sick", "unpaid", "other"]);
export type LeaveKind = z.infer<typeof LeaveKind>;

export const LEAVE_KIND_LABELS: Record<LeaveKind, string> = {
  daily: "استحقاقی روزانه",
  hourly: "استحقاقی ساعتی",
  sick: "استعلاجی",
  unpaid: "بدون حقوق",
  other: "سایر",
};

export const RecordLeaveInput = z.object({
  personId: Id,
  kind: LeaveKind,
  startDate: BusinessDate,
  endDate: BusinessDate,
  startTime: TimeString.nullable().optional().default(null),
  endTime: TimeString.nullable().optional().default(null),
  replacementPersonId: Id.nullable().optional().default(null),
  reason: Note.optional().default(""),
});
export type RecordLeaveInput = z.infer<typeof RecordLeaveInput>;

export const LeaveRecord = EntityBase.extend({
  personId: Id,
  personName: z.string(),
  kind: LeaveKind,
  startDate: BusinessDate,
  endDate: BusinessDate,
  startTime: z.string().nullable().optional(),
  endTime: z.string().nullable().optional(),
  replacementPersonId: Id.nullable().optional(),
  replacementPersonName: z.string().optional(),
  reason: z.string(),
});
export type LeaveRecord = z.infer<typeof LeaveRecord>;

export const AttendanceItemStatus = z.enum(["present", "late", "absent", "off", "on_leave", "missing_punch"]);
export type AttendanceItemStatus = z.infer<typeof AttendanceItemStatus>;

export const TodayAttendanceItem = z.object({
  personId: Id,
  personName: z.string(),
  positionId: Id.nullable(),
  positionName: z.string(),
  plannedShift: z.object({
    shiftTemplateId: Id.nullable(),
    shiftName: z.string(),
    startTime: z.string().nullable(),
    endTime: z.string().nullable(),
    isOff: z.boolean(),
  }),
  punches: z.array(AttendancePunch),
  firstIn: z.string().nullable(),
  lastOut: z.string().nullable(),
  status: AttendanceItemStatus,
  isLate: z.boolean().default(false),
  leave: LeaveRecord.optional(),
  pendingCorrection: PunchCorrectionRequest.optional(),
});
export type TodayAttendanceItem = z.infer<typeof TodayAttendanceItem>;

export const TodayAttendanceSummary = z.object({
  date: BusinessDate,
  totalStaff: z.number().int(),
  plannedCount: z.number().int(),
  presentCount: z.number().int(),
  lateCount: z.number().int(),
  absentCount: z.number().int(),
  offCount: z.number().int(),
  onLeaveCount: z.number().int(),
  missingPunchCount: z.number().int(),
  items: z.array(TodayAttendanceItem),
});
export type TodayAttendanceSummary = z.infer<typeof TodayAttendanceSummary>;

// ============================================================================
// Staff Notices (§11.3)
// ============================================================================

export const NoticeUrgency = z.enum(["normal", "important", "urgent"]);
export type NoticeUrgency = z.infer<typeof NoticeUrgency>;

export const NoticeKind = z.enum(["notice", "letter"]);
export type NoticeKind = z.infer<typeof NoticeKind>;

export const NoticeTargetType = z.enum(["person", "position", "station", "all"]);
export type NoticeTargetType = z.infer<typeof NoticeTargetType>;

export const StaffNoticeInput = z.object({
  kind: NoticeKind.default("notice"),
  title: z.string().max(200).optional().default(""),
  content: z.string().min(1).max(2000),
  urgency: NoticeUrgency.default("normal"),
  targetType: NoticeTargetType,
  targetId: Id.nullable().optional().default(null),
  targetName: z.string().optional().default(""),
  requiresAck: z.boolean().default(false),
});
export type StaffNoticeInput = z.infer<typeof StaffNoticeInput>;

export const StaffNotice = EntityBase.extend({
  kind: NoticeKind,
  title: z.string(),
  content: z.string(),
  urgency: NoticeUrgency,
  targetType: NoticeTargetType,
  targetId: Id.nullable(),
  targetName: z.string(),
  requiresAck: z.boolean(),
  senderPersonId: Id,
  senderPersonName: z.string(),
});
export type StaffNotice = z.infer<typeof StaffNotice>;

export const StaffNoticeAck = EntityBase.extend({
  noticeId: Id,
  personId: Id,
  personName: z.string(),
  ackedAt: z.string(),
});
export type StaffNoticeAck = z.infer<typeof StaffNoticeAck>;

export const StaffNoticeWithStats = StaffNotice.extend({
  totalRecipients: z.number().int(),
  ackedCount: z.number().int(),
  hasAcked: z.boolean().default(false),
  acks: z.array(StaffNoticeAck).optional().default([]),
  unackedStaff: z
    .array(z.object({ personId: Id, personName: z.string() }))
    .optional()
    .default([]),
});
export type StaffNoticeWithStats = z.infer<typeof StaffNoticeWithStats>;

// ============================================================================
// Temporary and Daily Workers (§11.7)
// ============================================================================

export const TempWorkerRateType = z.enum(["daily", "hourly"]);
export type TempWorkerRateType = z.infer<typeof TempWorkerRateType>;

export const TempWorkerPaymentMethod = z.enum(["cash", "card", "bank_transfer", "other"]);
export type TempWorkerPaymentMethod = z.infer<typeof TempWorkerPaymentMethod>;

export const TempWorkerInput = z.object({
  name: z.string().min(1).max(120),
  mobile: MobilePhone,
  nationalId: z.string().max(20).optional().default(""),
  rateType: TempWorkerRateType,
  rateAmount: Rial,
  notes: Note.optional().default(""),
});
export type TempWorkerInput = z.infer<typeof TempWorkerInput>;

export const TempWorker = EntityBase.extend({
  name: z.string(),
  mobile: MobilePhone,
  nationalId: z.string(),
  rateType: TempWorkerRateType,
  rateAmount: Rial.nullable(),
  notes: z.string(),
  active: z.boolean(),
});
export type TempWorker = z.infer<typeof TempWorker>;

export const TempWorkerWorkRecordInput = z.object({
  workerId: Id,
  date: BusinessDate,
  hoursWorked: z.number().min(0).max(24).optional().default(0),
  daysWorked: z.number().min(0).max(1).optional().default(1),
  eventId: Id.nullable().optional().default(null),
  notes: Note.optional().default(""),
});
export type TempWorkerWorkRecordInput = z.infer<typeof TempWorkerWorkRecordInput>;

export const MarkTempWorkerPaidInput = z.object({
  paymentMethod: TempWorkerPaymentMethod,
  paymentRef: z.string().max(100).optional().default(""),
  paidAmount: Rial.optional(),
});
export type MarkTempWorkerPaidInput = z.infer<typeof MarkTempWorkerPaidInput>;

export const TempWorkerWorkRecord = EntityBase.extend({
  workerId: Id,
  workerName: z.string(),
  workerMobile: MobilePhone,
  date: BusinessDate,
  rateType: TempWorkerRateType,
  rateAmount: Rial.nullable(),
  hoursWorked: z.number(),
  daysWorked: z.number(),
  eventId: Id.nullable(),
  calculatedWage: Rial.nullable(),
  notes: z.string(),
  isPaid: z.boolean(),
  paidAt: z.string().nullable(),
  paidAmount: Rial.nullable(),
  paymentMethod: TempWorkerPaymentMethod.nullable(),
  paymentRef: z.string().nullable(),
});
export type TempWorkerWorkRecord = z.infer<typeof TempWorkerWorkRecord>;

// ==========================================
// AG-44: Monthly Work Summary, Pay Run & Payslip
// ==========================================

export const PayRunStatus = z.enum(["draft", "approved"]);
export type PayRunStatus = z.infer<typeof PayRunStatus>;

export const PayLineCategory = z.enum([
  "base",
  "overtime",
  "holiday",
  "night",
  "benefit",
  "deduction",
  "adjustment",
]);
export type PayLineCategory = z.infer<typeof PayLineCategory>;

/** A payroll month is a Jalali month (آبان ۱۴۰۵ is 1405/8), as Iranian pay is. */
export const PayYear = z.number().int().min(1390).max(1499);
export const PayMonth = z.number().int().min(1).max(12);

const tehranJalali = new Intl.DateTimeFormat("en-US-u-ca-persian", {
  timeZone: "UTC",
  year: "numeric",
  month: "numeric",
  day: "numeric",
});

function jalaliOf(utcNoon: number): { y: number; m: number; d: number } {
  const parts = tehranJalali.formatToParts(new Date(utcNoon));
  const n = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { y: n("year"), m: n("month"), d: n("day") };
}

const DAY_MS = 86_400_000;
const isoOf = (utcNoon: number) => new Date(utcNoon).toISOString().slice(0, 10);

/** The Jalali pay month a business date belongs to (2026-11-10 → 1405/8). */
export function jalaliPayMonthOf(date: BusinessDate): { year: number; month: number } {
  const [y = 0, m = 1, d = 1] = date.split("-").map(Number);
  const j = jalaliOf(Date.UTC(y, m - 1, d, 12));
  return { year: j.y, month: j.m };
}

/** The Gregorian business dates of a Jalali month, first to last (1405/8 → 2026-10-23 … 2026-11-21). */
export function jalaliMonthDates(jYear: number, jMonth: number): BusinessDate[] {
  const offset = jMonth <= 6 ? (jMonth - 1) * 31 : 186 + (jMonth - 7) * 30;
  let t = Date.UTC(jYear + 621, 2, 21, 12) + (offset - 3) * DAY_MS;
  for (let i = 0; i < 8; i++, t += DAY_MS) {
    const j = jalaliOf(t);
    if (j.y === jYear && j.m === jMonth && j.d === 1) break;
  }
  const first = jalaliOf(t);
  if (first.y !== jYear || first.m !== jMonth)
    throw new Error(`no Jalali month ${String(jYear)}/${String(jMonth)}`);
  const dates: BusinessDate[] = [];
  for (; jalaliOf(t).m === jMonth; t += DAY_MS) dates.push(isoOf(t));
  return dates;
}

export const PayRunLine = z.object({
  code: z.string(),
  title: z.string(),
  category: PayLineCategory,
  quantity: z.number().optional().default(1),
  rate: z.number().optional().default(0),
  amountRial: Rial,
  note: z.string().optional().default(""),
});
export type PayRunLine = z.infer<typeof PayRunLine>;

export const PayRulesSettings = z.object({
  overtimeRatePerMille: z.number().int().min(1000).max(3000).default(1400),
  holidayRatePerMille: z.number().int().min(1000).max(3000).default(1400),
  nightRatePerMille: z.number().int().min(1000).max(3000).default(1350),
  standardMonthlyHours: z.number().int().min(100).max(250).default(176),
});
export type PayRulesSettings = z.infer<typeof PayRulesSettings>;

export const FixedBenefitDeductionInput = z.object({
  personId: Id,
  title: z.string().min(1).max(120),
  kind: z.enum(["benefit", "deduction"]),
  amountRial: Rial,
  notes: Note.optional().default(""),
});
export type FixedBenefitDeductionInput = z.infer<typeof FixedBenefitDeductionInput>;

export const FixedBenefitDeduction = EntityBase.extend({
  personId: Id,
  title: z.string(),
  kind: z.enum(["benefit", "deduction"]),
  amountRial: Rial,
  notes: z.string(),
  active: z.boolean(),
});
export type FixedBenefitDeduction = z.infer<typeof FixedBenefitDeduction>;

export const MonthlyWorkSummaryPerson = z.object({
  personId: Id,
  personName: Name,
  positionName: z.string(),
  employmentKind: EmploymentKind,
  basePay: Rial.nullable(),
  hourlyRate: Rial.nullable(),
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  holidayMinutes: z.number().int(),
  nightMinutes: z.number().int(),
  leaveDays: z.number(),
  leaveHours: z.number(),
  absenceDays: z.number().int(),
  lateMinutes: z.number().int(),
  missingPunchCount: z.number().int(),
});
export type MonthlyWorkSummaryPerson = z.infer<typeof MonthlyWorkSummaryPerson>;

export const MonthlyWorkSummary = z.object({
  year: PayYear,
  month: PayMonth,
  people: z.array(MonthlyWorkSummaryPerson),
});
export type MonthlyWorkSummary = z.infer<typeof MonthlyWorkSummary>;

export const PayRunItem = EntityBase.extend({
  payRunId: Id,
  personId: Id,
  personName: Name,
  positionName: z.string(),
  employmentKind: EmploymentKind,
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  holidayMinutes: z.number().int(),
  nightMinutes: z.number().int(),
  leaveDays: z.number(),
  leaveHours: z.number(),
  absenceDays: z.number().int(),
  lateMinutes: z.number().int(),
  baseRial: Rial,
  overtimeRial: Rial,
  holidayRial: Rial,
  nightRial: Rial,
  benefitsRial: Rial,
  deductionsRial: Rial,
  adjustmentsRial: Rial,
  grossRial: Rial,
  netRial: Rial,
  lines: z.array(PayRunLine),
});
export type PayRunItem = z.infer<typeof PayRunItem>;

export const PayRun = EntityBase.extend({
  year: PayYear,
  month: PayMonth,
  status: PayRunStatus,
  itemsCount: z.number().int(),
  totalBaseRial: Rial,
  totalOvertimeRial: Rial,
  totalHolidayRial: Rial,
  totalNightRial: Rial,
  totalBenefitsRial: Rial,
  totalDeductionsRial: Rial,
  totalAdjustmentsRial: Rial,
  totalGrossRial: Rial,
  totalNetRial: Rial,
  approvedAt: z.string().nullable(),
  approvedBy: z.string().nullable(),
  paidAt: z.string().nullable().optional(),
  paidBy: z.string().nullable().optional(),
  paymentMethod: z.enum(["bank", "cash"]).nullable().optional(),
  paymentReference: z.string().nullable().optional(),
  paidOn: z.string().nullable().optional(),
  notes: z.string(),
  items: z.array(PayRunItem).optional(),
});
export type PayRun = z.infer<typeof PayRun>;

export const MarkPayRunPaidInput = z.object({
  method: z.enum(["bank", "cash"]),
  paidOn: z.string(),
  reference: z.string().optional(),
});
export type MarkPayRunPaidInput = z.infer<typeof MarkPayRunPaidInput>;

export const CreatePayRunInput = z.object({
  year: PayYear,
  month: PayMonth,
  notes: Note.optional().default(""),
});
export type CreatePayRunInput = z.infer<typeof CreatePayRunInput>;

export const Payslip = z.object({
  payRunId: Id,
  year: z.number().int(),
  month: z.number().int(),
  personId: Id,
  personName: Name,
  positionName: z.string(),
  employmentKind: EmploymentKind,
  status: PayRunStatus,
  approvedAt: z.string().nullable(),
  workedMinutes: z.number().int(),
  overtimeMinutes: z.number().int(),
  holidayMinutes: z.number().int(),
  nightMinutes: z.number().int(),
  leaveDays: z.number(),
  leaveHours: z.number(),
  absenceDays: z.number().int(),
  lateMinutes: z.number().int(),
  baseRial: Rial,
  overtimeRial: Rial,
  holidayRial: Rial,
  nightRial: Rial,
  benefitsRial: Rial,
  deductionsRial: Rial,
  adjustmentsRial: Rial,
  grossRial: Rial,
  netRial: Rial,
  earningsLines: z.array(PayRunLine),
  deductionLines: z.array(PayRunLine),
  adjustmentLines: z.array(PayRunLine),
});
export type Payslip = z.infer<typeof Payslip>;
